<script setup lang="ts" generic="T">
import { VueTelInput } from 'vue-tel-input';
import 'vue-tel-input/vue-tel-input.css';

/********************
 * PROPS & EMITS     *
 ********************/
const props = withDefaults(defineProps<{
  inputId?: string;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  modelValue: T;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  inverted?: boolean;
}>(), {
  inputId: '',
  disabled: false,
  error: false,
  label: '',
  placeholder: '',
  readOnly: false,
  required: false,
  inverted: false,
});

const emit = defineEmits<{
  input: [value: string | number];
  blur: [value: Record<string, boolean>];
}>();

defineOptions({
  inheritAttrs: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
const $attrs = useAttrs();

/********************
 * REFS & VARS       *
 ********************/
const model = defineModel<T>();
const isValid = ref(true);

function onValidate(phoneObject: any) {
  if (phoneObject?.valid === undefined) {
    return;
  }
  if (phoneObject.number === '') {
    isValid.value = true;
  } else {
    isValid.value = !!phoneObject.valid;
  }
}

function onBlur(): void {
  emit('blur', { isValid: isValid.value });
}
</script>

<template>
  <div
    :class="[`input-wrapper--${props.inputId}`, error ? 'has-error' : '', $attrs.class]"
    class="mb-6 flex flex-col"
  >
    <div class="input-label__wrapper relative">
      <label
        :for="props.inputId"
        class="input-label text-left"
      >
        <span>{{ props.label }}<sup v-if="props.required">*</sup></span>
      </label>
      <slot />
    </div>

    <div class="input-field__wrapper relative flex grow">
      <VueTelInput
        v-model="model"
        :input-options="{ readonly: props.readOnly, showDialCode: false, autocomplete: 'on', autofocus: false, id: props.inputId, maxlength: 25, name: 'telephone' }"
        :dropdown-options="{ searchBoxPlaceholder: '', showFlags: true, showDialCodeInList: true, showDialCodeInSelection: false, showSearchBox: true, tabindex: 0 }"
        :disabled="props.disabled"
        :placeholder="props.placeholder"
        v-bind="$attrs"
        class="input-field input-field--text"
        :class="{ 'input--inverted': props.inverted }"
        mode="international"
        :valid-characters-only="true"
        :preferred-countries="['de', 'at', 'ch', 'nl', 'fr', 'es', 'it', 'gb', 'ie', 'cs', 'dk', 'se', 'no', 'fi', 'hr', 'sk', 'sv']"
        :error="props.error"
        @blur="onBlur"
        @validate="onValidate"
      />
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.input-field--text {
  @apply px-6 py-4;
}

.vue-tel-input {
  border-width: 0;
  height: 58px;
}

.vti__phone:focus {
  box-shadow: none;
}

.vue-tel-input:focus-within {
  box-shadow: none;
  outline: auto 1px Highlight;
  outline: auto 1px -webkit-focus-ring-color;
}

.vue-tel-input,
.vue-tel-input .vti__input {
  @apply bg-white;
}

.has-error .vue-tel-input {
  border: 1px solid rgb(var(--color-danger));
}
</style>

<style lang="postcss">
.vti__search_box {
  border: none;
}

.vue-tel-input.input--inverted,
.vue-tel-input.input--inverted .vti__input {
  @apply bg-gray-10;
}
</style>
